// Mitarbeitende als kleine Figuren auf dem Werkshof. Die Anzahl folgt der Mitarbeitendenzahl.
import * as THREE from "three"
import gsap from "gsap"
import { introduce } from "./bubble.js"
import { ROSTER, quoteFor } from "./roster.js"
import { COLORS } from "./palette.js"
import { person, SKIN_TONES } from "./builders.js"
import { rng } from "../game/rng.js"

const YARD = { hx: 9.6, hz: 7.2 }
const FIGURE_SCALE = 1.45
const TOUCH_DISTANCE = 0.8
const MAX_WALKERS = 3
const WALK_SPEED = 0.65

const HIT_MATERIAL = new THREE.MeshBasicMaterial({ visible: false })
const OUTFITS = {
  maschinenbau: { shirts: [COLORS.navy600, COLORS.slate, COLORS.green], helmets: [COLORS.white, COLORS.lime] },
  kunststoff: { shirts: [COLORS.white, COLORS.green, COLORS.navy600], helmets: [COLORS.white, COLORS.orange] },
  lebensmittel: { shirts: [COLORS.white], helmets: [COLORS.white] },
  bau: { shirts: [COLORS.orange, COLORS.lime], helmets: [COLORS.lime, COLORS.white, COLORS.orange] },
  handel: { shirts: [COLORS.lime, COLORS.navy600, COLORS.green], helmets: [null] },
  dienstleistung: { shirts: [COLORS.white, COLORS.slate, COLORS.green, COLORS.red, COLORS.navy600], helmets: [null] }
}

export function peopleFor(employees) {
  return Math.max(2, Math.round(2 + 8.3 * Math.log10(Math.max(employees, 10) / 10)))
}

export class People {
  constructor(factory, industry) {
    this.factory = factory
    this.industry = industry
    this.random = rng(industry.length * 7919)
    this.figures = []
    this.spots = this.findSpots()
  }

  // Freie Stellen auf dem Hof: nicht in Gebäuden, Fahrzeugen oder Bäumen.
  findSpots() {
    this.factory.updateMatrixWorld(true)
    this.obstacles = this.factory.children
      .filter((child) => !child.userData.yard && !child.userData.person)
      .map((child) => new THREE.Box3().setFromObject(child))
      .filter((box) => box.max.y > 0.2)
      .map((box) => box.expandByScalar(0.6))
    const spots = []
    for (let i = 0; i < 600 && spots.length < 40; i++) {
      const p = new THREE.Vector3((this.random() * 2 - 1) * YARD.hx, 0, (this.random() * 2 - 1) * YARD.hz)
      const reserved = (this.factory.userData.keepClear || []).some((c) => Math.hypot(p.x - c.x, p.z - c.z) < c.r)
      if (reserved || this.blocked(p) || spots.some((s) => s.distanceTo(p) < 1.6)) continue
      spots.push(p)
    }
    // Zuerst die Stellen vorne auf dem Hof belegen, die aus der Kamera gut zu sehen sind.
    return spots.sort((a, b) => b.z + b.x * 0.3 - (a.z + a.x * 0.3))
  }

  blocked(point) {
    const probe = new THREE.Vector3(point.x, 0.5, point.z)
    return this.obstacles.some((box) => box.containsPoint(probe))
  }

  pathFree(a, b) {
    for (let t = 0; t <= 1; t += 0.1) if (this.blocked(new THREE.Vector3().lerpVectors(a, b, t))) return false
    return true
  }

  createFigure(index) {
    const outfit = OUTFITS[this.industry] || OUTFITS.maschinenbau
    const pickFrom = (list) => list[Math.floor(this.random() * list.length)]
    const figure = person({ shirt: pickFrom(outfit.shirts), helmet: pickFrom(outfit.helmets), skin: pickFrom(SKIN_TONES) })
    figure.userData.person = true
    figure.userData.character = ROSTER[this.industry]?.[index] ?? { name: "Alex", role: "Springer:in", quotes: ["Ich bin heute nur Vertretung."] }
    figure.userData.size = FIGURE_SCALE
    figure.rotation.order = "YXZ"
    figure.userData.hop = 0
    figure.userData.cooldown = 0
    // Unsichtbare Klickzone, die Figuren selbst sind sehr klein
    const zone = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 1.3, 8), HIT_MATERIAL)
    zone.position.y = 0.65
    figure.add(zone)
    const home = this.spots[index]
    figure.position.copy(home)
    figure.scale.setScalar(FIGURE_SCALE)
    figure.rotation.y = this.random() * Math.PI * 2
    const partners = this.spots.filter((s) => s !== home && s.distanceTo(home) > 2.5 && s.distanceTo(home) < 7 && this.pathFree(home, s))
    const partner = partners[Math.floor(this.random() * partners.length)]
    // Nur wenige laufen herum, die meisten stehen und plaudern
    const walking = index % 5 === 2 && this.figures.filter((f) => f.userData.walk).length < MAX_WALKERS
    figure.userData.walk = walking && partner ? { from: home.clone(), to: partner.clone(), t: this.random(), dir: 1 } : null
    figure.userData.phase = this.random() * Math.PI * 2
    return figure
  }

  setCount(count, { animate = true } = {}) {
    const target = Math.min(count, this.spots.length)
    while (this.figures.length < target) {
      const figure = this.createFigure(this.figures.length)
      this.factory.add(figure)
      this.figures.push(figure)
      if (animate) {
        figure.scale.setScalar(0.001)
        gsap.to(figure.scale, { x: FIGURE_SCALE, y: FIGURE_SCALE, z: FIGURE_SCALE, duration: 0.5, delay: (this.figures.length % 6) * 0.05, ease: "back.out(3)" })
      }
    }
    while (this.figures.length > target) {
      const figure = this.figures.pop()
      if (!animate) {
        figure.removeFromParent()
        continue
      }
      gsap.killTweensOf(figure.scale)
      gsap.to(figure.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.25, onComplete: () => figure.removeFromParent() })
    }
  }

  // Zwei Figuren berühren sich: die laufende dreht einfach um.
  bump(a) {
    a.userData.cooldown = 2.4
    if (a.userData.walk) a.userData.walk.dir *= -1
  }

  // Klick auf eine Figur: Sprung mit Pirouette und ein Spruch.
  poke(figure) {
    if (figure.userData.poking) return
    figure.userData.poking = true
    gsap.fromTo(figure.userData, { hop: 0 }, { hop: 1.1, duration: 0.3, yoyo: true, repeat: 1, ease: "power2.out" })
    gsap.to(figure.rotation, { y: figure.rotation.y + Math.PI * 2, duration: 0.6, ease: "power2.inOut", onComplete: () => (figure.userData.poking = false) })
    const quote = quoteFor(figure.userData.character, this.industry, figure.userData.lastQuote)
    figure.userData.lastQuote = quote
    introduce(figure, figure.userData.character, quote)
  }

  pick(raycaster) {
    const hit = raycaster.intersectObjects(this.figures, true)[0]
    if (!hit) return null
    let object = hit.object
    while (object && !object.userData.person) object = object.parent
    return object
  }

  update(dt, time) {
    for (const figure of this.figures) {
      figure.userData.cooldown = Math.max(0, figure.userData.cooldown - dt)
      const walk = figure.userData.walk
      if (walk) {
        const length = walk.from.distanceTo(walk.to)
        walk.t += (walk.dir * WALK_SPEED * dt) / length
        if (walk.t > 1 || walk.t < 0) {
          walk.dir *= -1
          walk.t = THREE.MathUtils.clamp(walk.t, 0, 1)
        }
        figure.position.lerpVectors(walk.from, walk.to, walk.t)
        const heading = walk.dir > 0 ? walk.to : walk.from
        if (!figure.userData.poking) figure.rotation.y = Math.atan2(heading.x - figure.position.x, heading.z - figure.position.z)
        figure.position.y = Math.abs(Math.sin(time * 9 + figure.userData.phase)) * 0.06 + figure.userData.hop
      } else {
        figure.position.y = Math.max(0, Math.sin(time * 2.2 + figure.userData.phase)) * 0.05 + figure.userData.hop
        if (!figure.userData.poking) figure.rotation.y += Math.sin(time * 0.5 + figure.userData.phase) * 0.004
      }
    }
    // Zusammenstöße: nur Laufende lösen sie aus, Stehende werden angerempelt.
    for (const a of this.figures) {
      if (!a.userData.walk || a.userData.cooldown > 0 || a.scale.x < FIGURE_SCALE * 0.9) continue
      for (const b of this.figures) {
        if (a === b || b.userData.cooldown > 0 || b.scale.x < FIGURE_SCALE * 0.9) continue
        const dx = a.position.x - b.position.x
        const dz = a.position.z - b.position.z
        if (dx * dx + dz * dz < TOUCH_DISTANCE * TOUCH_DISTANCE) {
          this.bump(a, b)
          break
        }
      }
    }
  }
}
